import * as React from "react";
import { cn } from "../../lib/utils";

const TabsContext = React.createContext({ value: undefined, setValue: () => {} });

const Tabs = ({ value, defaultValue, onValueChange, className, children, ...props }) => {
  const [uncontrolled, setUncontrolled] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : uncontrolled;
  const setValue = (next) => {
    if (!isControlled) setUncontrolled(next);
    if (onValueChange) onValueChange(next);
  };
  return (
    <TabsContext.Provider value={{ value: current, setValue }}>
      <div className={cn("", className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    role="tablist"
    className={cn(
      "inline-flex h-11 items-center justify-center rounded-full border border-border bg-foreground/[0.03] p-1 text-muted-foreground",
      className
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef(({ className, value, onClick, ...props }, ref) => {
  const { value: current, setValue } = React.useContext(TabsContext);
  const active = current === value;
  return (
    <button
      type="button"
      ref={ref}
      role="tab"
      aria-selected={active}
      data-state={active ? "active" : "inactive"}
      onClick={(e) => {
        if (onClick) onClick(e);
        setValue(value);
      }}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:text-foreground",
        active && "ui-tabs-on",
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

const TabsContent = React.forwardRef(({ className, value, ...props }, ref) => {
  const { value: current } = React.useContext(TabsContext);
  if (current !== value) return null;
  return (
    <div
      ref={ref}
      role="tabpanel"
      data-state="active"
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...props}
    />
  );
});
TabsContent.displayName = "TabsContent";

export { Tabs, TabsList, TabsTrigger, TabsContent };
